import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/16/solid';
import { Table } from '@tanstack/react-table';
import classNames from 'classnames';
import ReactPaginate from 'react-paginate';

type TablePaginationFooterProps<T> = {
  tableInstance: Table<T>;
};

const TablePaginationFooter = <T,>({
  tableInstance,
}: TablePaginationFooterProps<T>) => {
  const {
    getCanPreviousPage,
    getCanNextPage,
    setPageIndex,
    nextPage,
    previousPage,
    getState,
    getRowCount,
    getPageCount,
  } = tableInstance;

  const {
    pagination: { pageSize, pageIndex },
  } = getState();

  const itemStart = pageIndex * pageSize + 1;
  let itemEnd = itemStart - 1 + pageSize;

  if (itemEnd > getRowCount()) {
    itemEnd = getRowCount();
  }

  return (
    <div className="my-5 flex items-center justify-between border-t border-backgroundDarkGray px-10">
      <div className="flex flex-1 justify-between">
        <button
          onClick={() => previousPage()}
          className={classNames(
            'relative inline-flex items-center gap-2 rounded-md pt-2 text-sm font-medium text-primaryGray',
            { '!text-inactiveGray': !getCanPreviousPage() },
          )}
          disabled={!getCanPreviousPage()}
        >
          <ArrowLeftIcon className="size-4" />
          Previous
        </button>
        {getPageCount() > 1 && (
          <div className="hidden sm:flex">
            <ReactPaginate
              breakLabel={
                <span className="pointer-events-none relative inline-flex cursor-pointer items-center px-4 pt-4 text-sm font-medium text-gray-500">
                  ...
                </span>
              }
              onPageChange={({ selected }) => setPageIndex(selected)}
              pageRangeDisplayed={3}
              marginPagesDisplayed={1}
              pageCount={getPageCount()}
              forcePage={pageIndex}
              renderOnZeroPageCount={() => null}
              containerClassName="relative z-0 inline-flex"
              pageLinkClassName="relative inline-flex items-center border-t-2 px-4 pt-4 text-sm font-medium border-transparent text-gray-500"
              activeLinkClassName="z-10 border-t-2 !border-secondaryBlue text-black"
              previousLinkClassName="hidden"
              nextLinkClassName="hidden"
            />
          </div>
        )}
        <button
          onClick={() => nextPage()}
          disabled={!getCanNextPage()}
          className={classNames(
            'relative inline-flex items-center gap-2 rounded-md pt-2 text-sm font-medium text-primaryGray',
            { '!text-inactiveGray': !getCanNextPage() },
          )}
        >
          Next
          <ArrowRightIcon className="size-4" />
        </button>
      </div>
    </div>
  );
};

export default TablePaginationFooter;
